/**
 * 🎯 messageHelpers - 메시지 패턴 표준화 유틸리티
 *
 * Ant Design App.useApp().message API를 래핑하여 표준화된 메시지 형식 제공
 * - 성공/에러/경고/정보 메시지 표준화
 * - 일관된 메시지 형식 유지
 */

import type { MessageInstance } from 'antd/es/message/interface';

/**
 * 메시지 타입
 */
export type MessageType = 'success' | 'error' | 'warning' | 'info';

/**
 * 표준화된 메시지 옵션
 */
export interface MessageOptions {
  type?: MessageType;
  duration?: number;
  prefix?: string; // 메시지 앞에 추가할 접두사
}

export interface MessageHelpers {
  showMessage: (content: string, options?: MessageOptions) => void;
  showSuccess: (content: string, duration?: number) => void;
  showError: (content: string, duration?: number) => void;
  showWarning: (content: string, duration?: number) => void;
  showInfo: (content: string, duration?: number) => void;
}

let registeredMessageApi: MessageInstance | null = null;

export function registerMessageApi(api: MessageInstance | null): void {
  registeredMessageApi = api;
}

function requireMessageApi(): MessageInstance {
  if (!registeredMessageApi) {
    throw new Error(
      'Business message API is not registered. Wrap the app in antd <App> and mount BusinessMessageBridge.'
    );
  }
  return registeredMessageApi;
}

/**
 * 표준화된 메시지 표시 함수
 */
export function showMessageWithApi(
  messageApi: MessageInstance,
  content: string,
  options: MessageOptions = {}
): void {
  const { type = 'info', duration = 3, prefix = '' } = options;
  const finalContent = prefix ? `${prefix} ${content}` : content;

  switch (type) {
    case 'success':
      messageApi.success(finalContent, duration);
      break;
    case 'error':
      messageApi.error(finalContent, duration);
      break;
    case 'warning':
      messageApi.warning(finalContent, duration);
      break;
    case 'info':
    default:
      messageApi.info(finalContent, duration);
      break;
  }
}

export function createMessageHelpers(messageApi: MessageInstance): MessageHelpers {
  return {
    showMessage: (content, options) => showMessageWithApi(messageApi, content, options),
    showSuccess: (content, duration = 3) =>
      showMessageWithApi(messageApi, content, { type: 'success', duration }),
    showError: (content, duration = 3) =>
      showMessageWithApi(messageApi, content, { type: 'error', duration }),
    showWarning: (content, duration = 3) =>
      showMessageWithApi(messageApi, content, { type: 'warning', duration }),
    showInfo: (content, duration = 3) =>
      showMessageWithApi(messageApi, content, { type: 'info', duration }),
  };
}

/**
 * 표준화된 메시지 표시 함수 (legacy — requires BusinessMessageBridge)
 */
export function showMessage(content: string, options: MessageOptions = {}): void {
  showMessageWithApi(requireMessageApi(), content, options);
}

/**
 * 성공 메시지 표시
 */
export function showSuccess(content: string, duration: number = 3): void {
  showMessage(content, { type: 'success', duration });
}

/**
 * 에러 메시지 표시
 */
export function showError(content: string, duration: number = 3): void {
  showMessage(content, { type: 'error', duration });
}

/**
 * 경고 메시지 표시
 */
export function showWarning(content: string, duration: number = 3): void {
  showMessage(content, { type: 'warning', duration });
}

/**
 * 정보 메시지 표시
 */
export function showInfo(content: string, duration: number = 3): void {
  showMessage(content, { type: 'info', duration });
}

/**
 * Field Editor 관련 표준 메시지
 */
export const FieldEditorMessages = {
  /**
   * 필드 추가 성공 메시지
   */
  fieldAdded: (fieldTypeLabel: string) => `새 ${fieldTypeLabel}가 추가되었습니다.`,

  /**
   * 필드 삭제 성공 메시지
   */
  fieldDeleted: (fieldTypeLabel: string) => `${fieldTypeLabel}가 삭제되었습니다.`,

  /**
   * 필드 수정 성공 메시지
   */
  fieldUpdated: (fieldTypeLabel: string) => `${fieldTypeLabel}가 수정되었습니다.`,

  /**
   * 필드 저장 성공 메시지
   */
  fieldSaved: () => '필드가 저장되었습니다.',

  /**
   * 필드 저장 실패 메시지
   */
  fieldSaveFailed: (reason?: string) =>
    reason ? `필드 저장에 실패했습니다: ${reason}` : '필드 저장에 실패했습니다.',
};

/**
 * 도메인 목록 로드 실패 메시지 (HTTP status별 사용자 표면)
 */
export function formatDomainLoadError(status: number | null, message: string): string {
  const trimmed = message.trim();
  const detail = trimmed ? ` — ${trimmed}` : '';

  switch (status) {
    case 403:
      return `이 영역에 접근할 권한이 없습니다.${detail}`;
    case 404:
      return `데이터 API 경로를 찾을 수 없습니다 (404). 관리자에게 문의하세요.${detail}`;
    case 400:
      return `요청이 올바르지 않습니다 (400)${detail}`;
    case 500:
    case 502:
    case 503:
      return `서버 오류로 데이터를 불러오지 못했습니다 (${status})${detail}`;
    default:
      if (status !== null) {
        return `데이터를 불러오지 못했습니다 (${status})${detail}`;
      }
      return trimmed ? `데이터를 불러오지 못했습니다. ${trimmed}` : '데이터를 불러오지 못했습니다.';
  }
}

/**
 * SurfaceState 전용 사용자 표면 메시지 (U2 — Resilient Surface)
 * 403 / not-configured / empty 등 상태별로 구분된 메시지를 반환한다.
 * generic "불러오지 못했습니다" 문구 금지 — 상태를 명확히 구분.
 */
export const SurfaceMessages = {
  forbidden: () => '이 영역에 접근할 권한이 없습니다.',
  notConfigured: () => '이 서브도메인이 아직 설정되지 않았습니다. 시스템 설정에서 활성화하세요.',
  empty: (domainLabel?: string) =>
    domainLabel ? `${domainLabel} 항목이 없습니다.` : '항목이 없습니다.',
  error: (status?: number) =>
    status ? `데이터를 불러오지 못했습니다 (${status}).` : '데이터를 불러오지 못했습니다.',
} as const;

/**
 * SurfaceState별 UI 표면 메시지 (Stream U2)
 *
 * 각 에러 상태에서 화면에 노출할 title + description 쌍.
 * Alert 문자열이 아닌 EmptyState 컴포넌트에 직접 주입한다.
 */
export const SurfaceErrorMessages: Record<
  'forbidden' | 'not-configured' | 'error',
  { title: string; description: string }
> = {
  forbidden: {
    title: '접근 권한이 없습니다',
    description: '이 데이터를 볼 권한이 없습니다. 관리자에게 권한을 요청하세요.',
  },
  'not-configured': {
    title: '아직 설정되지 않았습니다',
    description: '이 도메인의 스키마가 초기화되지 않았습니다. 관리자에게 설정을 요청하세요.',
  },
  error: {
    title: '데이터를 불러오지 못했습니다',
    description: '일시적인 오류가 발생했습니다. 새로고침을 시도해 주세요.',
  },
};

/**
 * errorStatus 코드에서 SurfaceErrorMessages 키를 도출한다.
 */
export function getSurfaceErrorKey(
  errorStatus: number | null
): 'forbidden' | 'not-configured' | 'error' {
  if (errorStatus === 403) return 'forbidden';
  return 'error';
}

/**
 * 일반적인 작업 메시지
 */
export const CommonMessages = {
  /**
   * 저장 성공
   */
  saved: () => '저장되었습니다.',

  /**
   * 저장 실패
   */
  saveFailed: () => '저장에 실패했습니다.',

  /**
   * 삭제 성공
   */
  deleted: () => '삭제되었습니다.',

  /**
   * 삭제 실패
   */
  deleteFailed: () => '삭제에 실패했습니다.',

  /**
   * 업데이트 성공
   */
  updated: () => '수정되었습니다.',

  /**
   * 업데이트 실패
   */
  updateFailed: () => '수정에 실패했습니다.',

  /**
   * 로드 실패
   */
  loadFailed: () => '데이터를 불러오지 못했습니다.',

  /**
   * 권한 없음
   */
  noPermission: () => '권한이 없습니다.',

  /**
   * 네트워크 에러
   */
  networkError: () => '네트워크 오류가 발생했습니다.',
};

/**
 * 메시지 헬퍼 전체 export
 */
export default {
  showMessage,
  showSuccess,
  showError,
  showWarning,
  showInfo,
  FieldEditorMessages,
  CommonMessages,
  SurfaceErrorMessages,
  getSurfaceErrorKey,
};
